<template>
    <div class="component json-page">
        <template v-if="!parseError">
            <v-card flat>
                <form autocomplete="off">
                    <v-img
                            v-if="headerImage"
                            v-bind:src="headerImage"
                            aspect-ratio="2.75"
                    ></v-img>

                    <v-card-title primary-title>
                        <div>
                            <div v-if="title" v-html="title"></div>
                        </div>
                    </v-card-title>

                    <v-card-text>
                        <div v-if="description" v-html="description"></div>

                        <div v-for="(config, key) in models" class="row" v-bind:key="key">
                            <template v-if="config.iterator_keys">
                                <template v-for="subKey in config.iterator_keys">
                                    <div class="col-12"
                                         :key="subKey" 
                                         v-if="modelPassesDisplayConditions(processConfig(config.config))">
                                        <model v-model="input[subKey]"
                                               v-on:onPageButtonClick="onPageButtonClick"
                                               v-bind:config="processConfig(config.config)"
                                               v-bind:id="subKey"
                                               v-bind:form-conditions="formConditions"
                                               v-bind:errors="getFormErrors(subKey)"
                                               v-on:input="onInputForKey(subKey, config.model)"></model>
                                    </div>
                                </template>
                            </template>
                            <template v-else-if="config.group">
                                <template v-for="(subConfig, subKey) in config.group">
                                    <div v-bind:key="subKey"
                                         v-if="modelPassesDisplayConditions(processConfig(subConfig))"
                                         v-bind:class="multiModelClasses(config.group)">
                                        <model v-model="input[subKey]"
                                               v-on:onPageButtonClick="onPageButtonClick"
                                               v-bind:config="processConfig(subConfig)"
                                               v-bind:id="subKey"
                                               v-bind:form-conditions="formConditions"
                                               v-bind:errors="getFormErrors(subKey)"
                                               v-on:input="onInputForKey(subKey, subConfig)"></model>
                                    </div>
                                </template>
                            </template>
                            <template v-else>
                                <div class="col-12" v-if="modelPassesDisplayConditions(processConfig(config))">
                                  <template v-if="config.type === 'row'">
                                    <div class="row">
                                      <div v-for="(childConfig, childKey) in processConfig(config).children" v-bind:class="childConfig.columnClasses">
                                        <model v-model="input[childKey]"
                                               v-on:onPageButtonClick="onPageButtonClick"
                                               v-bind:config="processConfig(childConfig)"
                                               v-bind:id="childKey"
                                               v-bind:form-conditions="formConditions"
                                               v-bind:errors="getFormErrors(childKey)"
                                               v-on:input="onInputForKey(childKey, childConfig)"></model>
                                      </div>
                                    </div>
                                  </template>
                                  <template v-else>
                                    <model v-model="input[key]"
                                           v-on:onPageButtonClick="onPageButtonClick"
                                           v-bind:config="processConfig(config)"
                                           v-bind:id="key"
                                           v-bind:form-conditions="formConditions"
                                           v-bind:errors="getFormErrors(key)"
                                           v-on:input="onInputForKey(key, config)"></model>
                                  </template>
                                </div>
                            </template>
                        </div>
                    </v-card-text>

                    <div class="card-footer">
                        <div v-if="footer" v-html="footer"></div>

                        <div class="text-right">
                            <div class="clearfix">
                                <template v-for="(config, index) in buttons">
                                    <page-button
                                            :key="`button_${index}`"
                                            v-bind:config="config"
                                            v-bind:class="config.classes"
                                            v-bind:form-conditions="formConditions"
                                            v-on:click="onPageButtonClick(config)"
                                    ></page-button>
                                </template>
                            </div>

                            <v-progress-circular
                                    v-if="isSubmitting"
                                    indeterminate
                                    color="primary"
                            ></v-progress-circular>
                        </div>

                        <div v-if="submissionErrorMessage" class="alert alert-danger">
                            {{ submissionErrorMessage }}
                        </div>
                    </div>
                </form>
            </v-card>
        </template>
        <template v-else>
            Invalid JSON
        </template>

        <div v-if="debug" class="debug mt-4">
            <hr/>
            <h4>Debug</h4>
            <div class="card">
                <div class="card-body">
                    <div class="error">
                        <span v-if="parseError">{{ parseError }}</span>
                    </div>
                    <div>
                        <h5>Go Back:</h5>
                        <pre>{{goBack}}</pre>
                        <h5>Back Button:</h5>
                        <pre>{{backButton}}</pre>
                        <h5>Access Token:</h5>
                        <pre>{{accessToken}}</pre>
                        <h5>Model Data:</h5>
                        <pre>{{ input }}</pre>
                        <h5>Form Data:</h5>
                        <pre>{{ formData }}</pre>
                    </div>
                    <template v-if="jsonString">
                        <h5>Form errors:</h5>
                        {{ formErrors }}
                        <hr/>
                        <h5>JSON String:</h5>
                        <textarea class="form-control" style="min-height: 5em;" v-model="jsonString"></textarea>
                        <hr/>
                        <h5>Conditions:</h5>
                        <ul>
                            <li>isFormCompleted: {{ isFormCompleted }}</li>
                        </ul>
                        <hr/>
                        <h5>Parsed JSON:</h5>
                        <pre>{{ parsedJSON }}</pre>
                        <hr/>
                        <h5>Variables:</h5>
                        <pre>{{ variables }}</pre>
                        <h5>Variable Errors:</h5>
                        <pre>{{ variableErrors }}</pre>
                        <h5>Display conditions results</h5>
                        <pre>{{displayConditionResults}}</pre>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
    import Model from './components/model'
    import PageButton from './components/button'
    import _isEmpty from 'lodash/isEmpty'
    import _uniq from 'lodash/uniq'
    import _set from 'lodash/set'
    import _get from 'lodash/get'
    import _each from 'lodash/each'
    import _debounce from 'lodash/debounce'
    import _merge from 'lodash/merge'
    import _find from 'lodash/find'
    import _isString from 'lodash/isString'
    import {VCard, VCardActions, VCardText, VCardTitle, VFooter, VImg, VProgressCircular} from 'vuetify/lib'
    import isEmail from 'validator/lib/isEmail';
    import isCreditCard from 'validator/lib/isCreditCard';

    import EventBus from '@/services/eventBus';
    import CamundaApiService from '@/services/camundaApi';
    import StringService from '@/services/string';

    import PasswordValidationMixin from '@/mixins/passwordValidation';
    import EventBusConstants from '@/constants/eventBus';

    const CONDITION_IS_FORM_COMPLETED = 'isFormCompleted';

    const VALIDATION_EVAL = 'eval';
    const VALIDATION_MATCH = 'match';
    const VALIDATION_MIN = 'min';
    const VALIDATION_MAX = 'max';
    const VALIDATION_REQUIRED_GROUP = 'isRequiredGroup';
    const VALIDATION_IS_PHONE_NUMBER = 'isPhoneNumber';
    const VALIDATION_IS_EMAIL = 'isEmail';
    const VALIDATION_IS_REQUIRED = 'isRequired';
    const VALIDATION_IS_CREDIT_CARD = 'isCreditCard';
    const VALIDATION_IS_MONTH_YEAR = 'isMonthYear';
    const VALIDATION_OKTA_PASSWORD_POLICY = "oktaPasswordPolicy";

    const DISPLAY_CONDITION_VARIABLE_EXISTS = 'variableExists';
    const DISPLAY_CONDITION_EVAL = 'eval';

    const ACTION_REDIRECT = 'redirect';
    const ACTION_BACK = 'back';
    const ACTION_COPY_INPUT = 'copyInput';
    const ACTION_SET_INPUT = 'setInput';
    const ACTION_CLEAR_INPUT = 'clearInput';
    const ACTION_SUBMIT = 'submit';
    const ACTION_SUBMIT_WITH_DATA = 'submitWithData';

    const ONLOAD_REDIRECT = 'redirect';


    export default {
        name: 'components-json_page-index',
        watch: {
            jsonString() {
                this.buildPage();
            },
            goBack(newValue) {
                if (newValue === true && this.backButton) {
                    this.onPageButtonClick(this.backButton);
                } else if (newValue === true && !this.backButton) {
                    console.log('Back requested, but no back button defined in JSON');
                }
            }
        },
        props: {
            jsonString: {
                default() {
                    return ''
                }
            },
            taskId: {
                type: String
            },
            goBack: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            debug: {
                default() {
                    return false
                }
            },
            variables: {
                default() {
                    return {};
                }
            }
        },
        data() {
            return {
                displayConditionResults: [],
                isSubmitting: false,
                input: {},
                formErrors: {},
                parseError: undefined,
                submissionErrorMessage: undefined
            }
        },
        methods: {
            multiModelClasses(group) {
                let classes = [];
                let columnWidth = Math.floor(12 / Object.keys(group).length);

                classes.push(`col-${columnWidth}`);

                return classes;
            },
            onPageButtonClick(config) {
                switch (config.action) {
                    case ACTION_SET_INPUT:
                      _each(_get(config, 'input', {}), (value, targetKey) => {
                        let targetConfig = _get(this.modelsFlat, targetKey, {});
                        this.$set(this.input, targetKey, value);
                        this.onInputForKey(targetKey, targetConfig);
                      });
                      break;
                    case ACTION_COPY_INPUT:
                      _each(_get(config, 'mapping', {}), (targetKey, sourceKey) => {
                        let source = _get(this.input, sourceKey);
                        let targetConfig = _get(this.modelsFlat, targetKey, {});
                        this.$set(this.input, targetKey, source);
                        this.onInputForKey(targetKey, targetConfig);
                      });
                      break;
                    case ACTION_CLEAR_INPUT:
                      _each(_get(config, 'keys', []), (targetKey) => {
                        let targetConfig = _get(this.modelsFlat, targetKey, {});
                        let newValue = undefined;
                        this.$set(this.input, targetKey, newValue);
                        this.onInputForKey(targetKey, targetConfig);
                      });
                      break;
                    case ACTION_BACK:
                    case ACTION_SUBMIT_WITH_DATA:
                        let formData = _get(config, 'data.body');

                        if (_get(config, 'data.merge') === true) {
                            formData = _merge(formData, this.formData);
                        }

                        this.submit(formData);
                        break;
                    case ACTION_SUBMIT:
                        this.submit();
                        break;
                    case ACTION_REDIRECT:
                        this.doRedirect(_get(config, 'url'));
                        break;
                    default:
                        console.log('Unknown button action', config.action);
                        break;
                }
            },
            onInputForKey(key, config) {
                if (this.isKeyEmpty(key)) {
                    this.formErrors[key] = [];
                    this.setValidationErrorsForKey(key, [
                        VALIDATION_REQUIRED_GROUP,
                        VALIDATION_IS_REQUIRED
                    ]);
                    return;
                }

                this.setValidationErrorsForKey(key);
                this.validateMatchesForKey(key);
                this.validateDependantsOfKey(key);

                let linkedKeys = config['linked_keys'];
                if (linkedKeys) {
                    let value = _get(this.input, key, '');
                    this.updateLinkedKeysDebounced(linkedKeys, value);
                }
            },
            updateLinkedKeysDebounced: _debounce(function (linkedKeys, value) {
                this.updateLinkedKeys(linkedKeys, value);
            }, 250),
            updateLinkedKeys(linkedKeys, value) {
                linkedKeys = Array.isArray(linkedKeys) ? linkedKeys : [linkedKeys];
                linkedKeys.forEach((linkedKey) => {
                    if (linkedKey.indexOf('.') > -1) {
                        let keyParts = linkedKey.split('.');
                        let linkedKeyIndex = keyParts.shift();
                        let nestedKey = keyParts.join('.');
                        let objectValue = _set({}, nestedKey, value);

                        this.$set(this.input, linkedKeyIndex, objectValue);
                    } else {
                        this.$set(this.input, linkedKey, value);
                    }

                    this.setValidationErrorsForKey(linkedKey);
                });
            },
            buildPage() {
                this.displayConditionResults = [];

                this.setPreFilledValuesFromConfig();
                this.setPreFilledValuesFromInstanceVariables();
                this.setErrorsFromVariable();
                this.handleOnload();
            },
            setValidationErrorsForAll() {
                this.modelKeys.forEach((key) => {
                    this.validateKey(key);
                });
            },
            setValidationErrorsForKey(key, validationsFilter = undefined) {
                this.formErrors[key] = this.validateKey(key, validationsFilter);
            },
            validateDependantsOfKey(key) {
                let dependantKeysWithValidators = {};

                _each(this.modelsFlat, (value, dependantKey) => {
                    if (dependantKey === key) {
                      return;
                    }

                    let dependantModel = _get(this.modelsFlat, dependantKey);
                    let dependantValidation = _get(dependantModel, 'validations', []);
                    let isDependant = false;
                    let dependantValidators = [];

                    dependantValidation.forEach((validator) => {
                        let isDependantValidator = false;
                        let parts = validator.split(':');
                        if (parts[0] === VALIDATION_MATCH && parts[1] === key) {
                          isDependantValidator = true;
                        }

                        if (isDependantValidator) {
                          isDependant = true;
                          dependantValidators.push(parts[0]);
                        }
                    });

                    if (isDependant) {
                      dependantKeysWithValidators[dependantKey] = dependantValidators;
                    }
                });

                _each(dependantKeysWithValidators, (validators, dependantKey) => {
                  this.setValidationErrorsForKey(dependantKey, validators);
                });
            },
            validateMatchesForKey(key) {
                let validations = _get(this.modelsFlat, `${key}.validations`, []).slice(0);

                validations.forEach((validation) => {
                    let validationParts = validation.split(':');
                    if (validationParts[0] === VALIDATION_MATCH) {
                        this.setValidationErrorsForKey(validationParts[1]);
                    }
                });
            },
            isKeyRequired(key) {
                return _get(this.modelsFlat, `${key}.required`);
            },
            isKeyRequiredGroup(key) {
                return _get(this.modelsFlat, `${key}.required_group`);
            },
            requiredGroupHasAValue(group) {
                let hasAValue = false;
                let keys = this.requiredGroupModelKeys(group);

                keys.forEach((key) => {
                    if (!this.isKeyEmpty(key)) {
                        hasAValue = true;
                    }
                });

                return hasAValue;
            },
            requiredGroupModelKeys(group) {
                let keys = [];

                _each(this.modelsFlat, (config, key) => {
                    let requiredGroup = _get(config, 'required_group');

                    if (!requiredGroup || requiredGroup !== group) {
                        return;
                    }

                    keys.push(key);
                });

                return keys;
            },
            requiredGroupClearErrors(group) {
                let errorMessage = this.requiredGroupErrorMessage(group);

                this.requiredGroupModelKeys(group).forEach((key) => {
                    let formKeyErrors = _get(this.formErrors, key);

                    if (!formKeyErrors) {
                        return;
                    }

                    let index = formKeyErrors.indexOf(errorMessage);
                    if (index > -1) {
                        this.formErrors[key].splice(index, 1);
                    }
                });
            },
            requiredGroupErrorMessage(group) {
                let labels = this.requiredGroupModelKeys(group).map((key) => {
                    let config = this.modelsFlat[key];
                    return config.label;
                });

                return `At least one of the following is required: ${labels.join(', ')}`;
            },
            isKeyEmpty(key) {
                let modelConfig = this.modelsFlat[key];
                let value = _get(this.input, key, "");
                if (modelConfig.type === "boolean" && typeof value === "boolean" && value === true) {
                    return false;
                } else if ((modelConfig.type === "select" || modelConfig.type === "radio" ) && typeof value === "boolean") {
                    return false;
                } else if (!value) {
                    return true;
                }

                return false;
            },
            /**
             * @param key
             * @param validationsFilter Only run validations that array in array
             * @returns {Array}
             */
            validateKey(key, validationsFilter = undefined) {
                let modelConfig = this.modelsFlat[key];
                let validations = _get(modelConfig, `validations`, []).slice(0);
                let value = _get(this.input, key, '');

                if (this.isKeyRequired(key)) {
                    validations.push('isRequired');
                }

                if (this.isKeyRequiredGroup(key)) {
                    validations.push('isRequiredGroup');
                }

                let errors = [];
                validations.forEach((validation) => {
                     if (validation === VALIDATION_OKTA_PASSWORD_POLICY) {
                        errors = this.oktaPasswordPolicyValidator(value, errors);
                    } else {
                        let validationParts = validation.split(':');

                        if (validationsFilter && validationsFilter.indexOf(validationParts[0]) === -1) {
                            return;
                        }

                        switch (validationParts[0]) {
                            case VALIDATION_EVAL:
                                if (value) {
                                    let errorMessage = validationParts[1];
                                    let evalString = validationParts[2];
                                    let finalEvalString = `(function() {let $value = "${value}"; let $instanceVariables = ${JSON.stringify(this.instanceVariables)}; ${evalString}})();`;

                                    try {
                                        let evalResult = eval(finalEvalString);
                                        if (!evalResult) {
                                            errors.push(errorMessage);
                                        }
                                    } catch (e) {
                                        console.warn('Eval string threw exception', finalEvalString, e);
                                    }
                                }
                                break;
                            case VALIDATION_MATCH:
                                if (value !== _get(this.input, validationParts[1])) {
                                    let matchModel = this.modelsFlat[validationParts[1]];
                                    errors.push(`Must match ${matchModel.label}`);
                                }
                                break;
                            case VALIDATION_MIN:
                                if (value && value.length < validationParts[1]) {
                                    errors.push(`Cannot be less than ${validationParts[1]} characters`);
                                }
                                break;
                            case VALIDATION_MAX:
                                if (value && value.length > validationParts[1]) {
                                    errors.push(`Cannot exceed ${validationParts[1]} characters`);
                                }
                                break;
                            case VALIDATION_IS_PHONE_NUMBER:
                                if (value && value.length !== 10) {
                                    errors.push('A valid phone number is required');
                                }
                                break;
                            case VALIDATION_IS_EMAIL:
                                if (value && !isEmail(value)) {
                                    errors.push('A valid email address is required');
                                }
                                break;
                            case VALIDATION_IS_MONTH_YEAR:
                                let dateParts = value.split('/');

                                if (dateParts.length !== 2
                                    || dateParts[0] > 12 || dateParts[0] < 1
                                    || dateParts[1].length !== 2) {
                                    errors.push('A valid month/year date is required');
                                }
                                break;
                            case VALIDATION_IS_CREDIT_CARD:
                                if (value && !isCreditCard(value)) {
                                    errors.push('A valid credit card number is required');
                                }
                                break;
                            case VALIDATION_IS_REQUIRED:
                                if (this.isKeyEmpty(key)) {
                                    errors.push('This field is required');
                                }
                                break;
                            case VALIDATION_REQUIRED_GROUP:
                                let group = modelConfig.required_group;

                                if (!this.requiredGroupHasAValue(group)) {
                                    errors.push(this.requiredGroupErrorMessage(group));
                                } else {
                                    this.requiredGroupClearErrors(group);
                                }
                                break;
                        }
                    }
                });

                return errors;
            },
            oktaPasswordPolicyValidator(value, errors) {
                if (this.passwordValidationComplexity.minLength) {
                    let isValid = this.validatePasswordMinLength(value);
                    if (isValid !== true) 
                        errors.push(isValid);                          
                }
                if (this.passwordValidationComplexity.minLowerCase) {
                    let isValid = this.validatePasswordMinLowerCase(value);
                    if (isValid !== true) 
                        errors.push(isValid); 
                }
                if (this.passwordValidationComplexity.minUpperCase) {
                    let isValid = this.validatePasswordMinUpperCase(value);
                    if (isValid !== true)
                        errors.push(isValid);
                }
                if (this.passwordValidationComplexity.minNumber) {
                    let isValid = this.validatePasswordMinNumber(value);
                    if (isValid !== true)
                        errors.push(isValid);
                }
                if (this.passwordValidationComplexity.minSymbol) {
                    let isValid = this.validatePasswordMinSymbol(value);
                    if (isValid !== true)
                        errors.push(isValid);
                }
                EventBus.$emit(EventBusConstants.OKTA_PASSWORD_POLICIES_STATUS, this.passwordValidPolicies);
                return errors;                        
            },
            handleOnload() {
                if (!this.onload) {
                    return;
                }

                let action = _get(this.onload, 'action');
                switch (action) {
                    case ONLOAD_REDIRECT:
                        this.onloadRedirect();
                        break;
                    default:
                        console.log('Unknown onload action');
                        break;
                }
            },
            onloadRedirect() {
                let delay = _get(this.onload, 'delay', 0);
                let url = _get(this.onload, 'url');

                setTimeout(() => {
                    this.doRedirect(url);
                }, delay);
            },
            doRedirect(url = undefined) {
                if (!this.taskId) {
                    return console.log('Would have redirected');
                }

                CamundaApiService.postTaskForm(this.taskId, this.formData)
                    .then(() => {
                        this.$emit('redirect', url);
                    });
            },
            setErrorsFromVariable() {
                if (this.variableErrors) {
                    _each(this.variableErrors, (value, key) => {
                        this.formErrors[key] = value;
                    });
                }
            },
            setPreFilledValuesFromInstanceVariables() {
                _each(this.instanceVariables, (value, key) => {
                    if (this.modelsFlat[key]) {
                        this.setPreFilledValueForKey(key, value);
                    }
                });
            },
            setPreFilledValuesFromConfig() {
                _each(this.modelsFlat, (config, key) => {
                    if (config.hasOwnProperty('value')) {
                        this.setPreFilledValueForKey(key, config.value);
                    }
                });
            },
            setPreFilledValueForKey(key, value) {
                let config = this.modelsFlat[key];

                if (config.mask && _isString(value)) {
                    // make sure only unmasked values make it in a masked input field
                    value = value.replace(/[^a-zA-Z0-9]/g, '');
                }

                this.$set(this.input, key, value);
            },
            getFormErrors(key) {
                return _get(this.formErrors, key, []);
            },
            processConfig(config) {
                if (config.type === 'html' && !config.sources) {
                    config.content = StringService.stringVariableSwap(config.content, this.mergedVariables);
                }

                return config;
            },
            submit(formData = undefined) {
                if (!formData) {
                    formData = this.formData;
                }

                if (!this.taskId) {
                    console.log('no task ID, firing submitted directly', formData);
                    this.$emit('submitted');
                    return;
                }

                this.isSubmitting = true;
                this.submissionErrorMessage = undefined;

                CamundaApiService.postTaskForm(this.taskId, formData)
                    .then((res) => {
                        this.$emit('submitted');
                    })
                    .catch((err) => {
                        let message = _get(err, 'response.data.message');

                        if (!message) {
                            message = err.toString();
                        }

                        this.$emit('exception', message);

                        this.submissionErrorMessage = message;
                    })
                    .finally(() => {
                        this.isSubmitting = false;
                    });
            },
            modelPassesDisplayConditions(config) {
                let conditions = _get(config, 'display_conditions', []);
                let matchesAll = true;

                conditions.forEach((condition) => {
                    let type = _get(condition, 'type');
                    switch (type) {
                        case DISPLAY_CONDITION_EVAL:
                            let evalString = _get(condition, 'evalString');
                            let finalEvalString = `(function() {let $instanceVariables = ${JSON.stringify(this.instanceVariables)}; ${evalString}})();`;

                            try {
                                let evalResult = eval(finalEvalString);
                                if (!evalResult) {
                                    matchesAll = false;
                                }
                            } catch (e) {
                                console.warn('Eval string threw exception', finalEvalString, e);
                            }
                            break;
                        case DISPLAY_CONDITION_VARIABLE_EXISTS:
                            let key = _get(condition, 'key');
                            let variable = _get(this.instanceVariables, key);

                            if (!variable) {
                                matchesAll = false;
                            }
                            break;
                    }
                });

                return matchesAll;
            }
        },
        computed: {
            formConditions() {
                return {
                    isSubmitting: this.isSubmitting,
                    isFormCompleted: this.isFormCompleted
                }
            },
            backButton() {
                return _find(this.buttons, (button) => {
                    return button.action === ACTION_BACK;
                });
            },
            onload() {
                return _get(this.parsedJSON, 'onload');
            },
            instanceVariables() {
                let mapped = {};
                let instanceVariables = _get(this.variables, 'instanceVariables', {});
                _each(instanceVariables, (value, key) => {
                    mapped[key] = value.value;
                });

                return mapped;
            },
            mergedVariables() {
                return _merge(this.variables, this.instanceVariables);
            },
            variableErrors() {
                return _get(this.instanceVariables, 'errors', {});
            },
            variableAccessToken() {
                return _get(this.instanceVariables, 'access_token');
            },
            parameterAccessToken() {
                return _get(this.$route, 'query.access_token');
            },
            accessToken() {
                return this.variableAccessToken || this.parameterAccessToken;
            },
            parsedJSON() {
                this.parseError = undefined;

                try {
                    return JSON.parse(this.jsonString);
                } catch (e) {
                    this.parseError = e;
                    return undefined;
                }
            },
            title() {
                return StringService.stringVariableSwap(_get(this.parsedJSON, 'title'), this.mergedVariables);
            },
            models() {
                let models = _get(this.parsedJSON, 'models', {});

                // Swap values that start with {{-}}
                _each(models, (config, modelKey) => {
                    _each(config, (value, configKey) => {
                        if (typeof value === "string" && value.indexOf('{{') === 0) {
                            let variableName = value.substr(2).slice(0, -2);
                            models[modelKey][configKey] = _get(this.mergedVariables, variableName, undefined);
                        }
                    });
                });

                return models;
            },
            description() {
                return StringService.stringVariableSwap(_get(this.parsedJSON, 'description'), this.mergedVariables);
            },
            headerImage() {
                return _get(this.parsedJSON, 'header_image');
            },
            buttons() {
                return _get(this.parsedJSON, 'buttons');
            },
            footer() {
                return StringService.stringVariableSwap(_get(this.parsedJSON, 'footer'), this.mergedVariables);
            },
            isFormCompleted() {
                let hasErrors = false;

                this.modelKeys.forEach((key) => {
                    let requiredGroup = this.isKeyRequiredGroup(key);
                    if (!requiredGroup && !this.isKeyRequired(key) && this.isKeyEmpty(key)) {
                        return;
                    } else if (requiredGroup && this.requiredGroupHasAValue(requiredGroup) && this.isKeyEmpty(key)) {
                        return;
                    }

                    let errors = this.validateKey(key);
                    if (errors.length > 0) {
                        hasErrors = true;
                    }
                });

                return !hasErrors;
            },
            modelsFlat() {
                let models = {};

                _each(this.models, (config, key) => {
                    if (config.hasOwnProperty('iterator_keys')) {
                        _each(config.iterator_keys, (subKey) => {
                            models[subKey] = config.config;
                        });
                    } else if (config.hasOwnProperty('group')) {
                        _each(config.group, (subConfig, subKey) => {
                            models[subKey] = subConfig;
                        });
                    } else if (config.hasOwnProperty('children')) {
                        _each(config.children, (subConfig, subKey) => {
                            models[subKey] = subConfig;
                        });
                    } else {
                        models[key] = config;
                    }
                });

                return models;
            },
            modelKeys() {
                return Object.keys(this.modelsFlat);
            },
            formData() {
                let data = {};
                let variables = {};

                _each(this.input, (value, key) => {
                    let modelConfig = this.modelsFlat[key];

                    variables[`${key}`] = CamundaApiService.encodeVariable(value);
                });

                if (this.accessToken) {
                    variables['access_token'] = CamundaApiService.encodeVariable(this.accessToken);
                }

                data.variables = variables;
                return data;
            }
        },
        created() {
            this.buildPage();
            // at the moment we do not create users with password so no need to retrieve the password settings
            //this.passwordValidationRetrievePasswordSettings();
        },
        mounted() {
        },
        beforeDestroy() {
        },
        mixins: [
            PasswordValidationMixin
        ],
        components: {
            Model,
            PageButton,
            VImg,
            VCard,
            VCardTitle,
            VCardText,
            VCardActions,
            VProgressCircular,
            VFooter
        }
    }

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
    .json-page {

    }

    .debug {

    }
</style>
